import React, { useState } from 'react';

import { Box, Step, StepLabel, Stepper } from '@mui/material';

import type { CppPartner, PartnerEarnBulkResult, PartnerEarnCsvRow } from '~/services/LuxLoyalty';

import BulkUploadStep from './steps/BulkUploadStep';
import ManualCreditStep from './steps/ManualCreditStep';
import ResultStep from './steps/ResultStep';
import ReviewStep from './steps/ReviewStep';
import SelectPartnerStep, { type PartnerEarnMethod } from './steps/SelectPartnerStep';

const BULK_STEPS = ['Select Partner', 'Upload CSV', 'Review', 'Result'];
const MANUAL_STEPS = ['Select Partner', 'Credit', 'Result'];

export default function PartnerEarnWizard() {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedMethod, setSelectedMethod] = useState<PartnerEarnMethod | null>(null);
  const [selectedPartner, setSelectedPartner] = useState<CppPartner | null>(null);
  const [rows, setRows] = useState<Array<PartnerEarnCsvRow>>([]);
  const [submitResult, setSubmitResult] = useState<PartnerEarnBulkResult | null>(null);

  const steps = selectedMethod === 'manual' ? MANUAL_STEPS : BULK_STEPS;

  const handleProceedToReview = (csvRows: Array<PartnerEarnCsvRow>) => {
    setRows(csvRows);
    setActiveStep(2);
  };

  const handleSubmitSuccess = (response: PartnerEarnBulkResult) => {
    setSubmitResult(response);
    setActiveStep(selectedMethod === 'manual' ? 2 : 3);
  };

  const handleStartNew = () => {
    setActiveStep(0);
    setSelectedMethod(null);
    setSelectedPartner(null);
    setRows([]);
    setSubmitResult(null);
  };

  return (
    <Box>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <SelectPartnerStep
          selectedMethod={selectedMethod}
          selectedPartner={selectedPartner}
          onSelectMethod={setSelectedMethod}
          onSelectPartner={setSelectedPartner}
          onNext={() => setActiveStep(1)}
        />
      )}

      {activeStep === 1 && selectedMethod === 'bulk' && selectedPartner && (
        <BulkUploadStep
          partner={selectedPartner}
          onBack={() => setActiveStep(0)}
          onProceedToReview={handleProceedToReview}
        />
      )}

      {activeStep === 1 && selectedMethod === 'manual' && selectedPartner && (
        <ManualCreditStep
          partner={selectedPartner}
          onBack={() => setActiveStep(0)}
          onSubmitSuccess={handleSubmitSuccess}
        />
      )}

      {activeStep === 2 && selectedMethod === 'bulk' && selectedPartner && (
        <ReviewStep
          rows={rows}
          partner={selectedPartner}
          onBack={() => setActiveStep(1)}
          onSubmitSuccess={handleSubmitSuccess}
        />
      )}

      {((selectedMethod === 'bulk' && activeStep === 3) || (selectedMethod === 'manual' && activeStep === 2)) &&
        submitResult &&
        selectedPartner && (
          <ResultStep result={submitResult} partnerId={selectedPartner.id} onStartNew={handleStartNew} />
        )}
    </Box>
  );
}
